const MIN_WIDTH = 20
const MIN_HEIGHT = 20
const MIN_RATIO = .6
const MAX_RATIO = .8

class LocationVector {

    constructor(element) {
        this.element = element

        this.ref = ['svg', 'vectorInput', 'box', 'handle', 'x', 'y', 'width', 'height', 'preview']
            .reduce((acc, item) => {
                acc[item] = this.element.querySelector(`[data-ref="${item}"]`)
                return acc
            }, {})

        this.ref.path = this.ref.svg.querySelector('path')

        this.viewBox = this.getViewBox()

        this.ref.box.addEventListener('mousedown', ::this.handleBoxMouseDown)
        this.ref.handle.addEventListener('mousedown', ::this.handleHandleMouseDown)
        this.element.addEventListener('mouseup', ::this.handleMouseUp)
        this.element.addEventListener('mousemove', ::this.handleMouseMove)
        this.element.addEventListener('mouseleave', ::this.handleMouseLeave)
        this.ref.vectorInput.addEventListener('change', ::this.handleVectorChange)

        this.drag = {
            active: false,
            clientX: 0,
            clientY: 0,
            boxX: 0,
            boxY: 0,
            type: 'box'
        }

        this.scale = 1

        const box = this.ref.box.getBoundingClientRect()

        this.box = {
            x: 0,
            y: 0,
            width: box.width,
            height: box.height
        }

        this.focusPath()
        this.init()

    }

    init() {
        let [x, y, width, height] = [
            'x',
            'y',
            'width',
            'height'
        ].map(key => this.ref[key].value)
        .map(value => parseInt(value))
        .map(value => isNaN(value) ? 0 : value)

        const polygonBox = this.ref.path.getBoundingClientRect()
        const previewBox = this.ref.preview.getBoundingClientRect()

        // accept scale
        width = width * this.scale
        height = height * this.scale
        x = x * this.scale
        y = y * this.scale

        // base offset of polygon
        const offset = {
            x: polygonBox.left - previewBox.left,
            y: polygonBox.top - previewBox.top
        }

        x += offset.x
        y += offset.y

        if (width === 0 || height === 0) {
            width = Math.round(polygonBox.width)
            height = Math.round(polygonBox.height)
        }

        this.box = {
            x,
            y,
            width,
            height
        }

        this.renderBox()
    }

    handleVectorChange(event) {
        this.setPath(event.target.value)
    }

    handleBoxMouseDown(event) {
        this.dragStart({
            x: event.clientX,
            y: event.clientY,
            type: 'box'
        })
    }

    handleHandleMouseDown(event) {
        event.stopPropagation()
        this.dragStart({
            x: event.clientX,
            y: event.clientY,
            type: 'handle'
        })
    }

    handleMouseUp() {
        this.dragEnd()
    }

    handleMouseMove(event) {
        if (!this.drag.active) {
            return
        }

        this.dragMove({
            x: event.clientX,
            y: event.clientY
        })
    }

    handleMouseLeave() {
        this.dragEnd()
    }

    dragStart({x, y, type}) {
        this.drag.active = true
        this.drag.clientX = x
        this.drag.clientY = y
        this.drag.type = type
        this.drag.boxX = this.box.x
        this.drag.boxY = this.box.y
        this.drag.boxWidth = this.box.width
        this.drag.boxHeight = this.box.height
    }

    dragMove({x, y}) {
        if (this.drag.type === 'box') {
            this.box.x = this.drag.boxX + x - this.drag.clientX
            this.box.y = this.drag.boxY + y - this.drag.clientY
        }

        if (this.drag.type === 'handle') {
            this.box.width = Math.max(MIN_WIDTH, this.drag.boxWidth + x - this.drag.clientX)
            this.box.height = Math.max(MIN_HEIGHT, this.drag.boxHeight + y - this.drag.clientY)
        }
        this.renderBox()
    }

    dragEnd() {
        this.drag.active = false

        this.save()
    }

    renderBox() {
        this.ref.box.style.top = `${this.box.y}px`
        this.ref.box.style.left = `${this.box.x}px`
        this.ref.box.style.width = `${this.box.width}px`
        this.ref.box.style.height = `${this.box.height}px`
    }

    getViewBox() {
        const [x, y, width, height] = this.ref.svg.getAttribute('viewBox').split(' ').map(value => parseInt(value))

        return {
            x,
            y,
            width,
            height
        }
    }

    focusPath() {
        const box = this.ref.path.getBBox()
        const { width, height } = this.viewBox

        // fit in area
        let scale = 1
        const ratio = box.width/box.height

        if (box.width < width * MIN_RATIO && box.height < height * MIN_RATIO) {
            scale = ratio > 1 ? width * MIN_RATIO/box.width : height * MIN_RATIO/box.height
        }

        if (box.width > width * MAX_RATIO || box.height > height * MAX_RATIO) {
            scale = ratio > 1 ? width * MAX_RATIO / box.width : height * MAX_RATIO / box.height
        }

        let x = (box.x - (width / scale - box.width)/2)
        let y = (box.y - (height / scale - box.height)/2)

        this.scale = scale

        this.ref.svg.setAttribute('viewBox', `${x} ${y} ${width / scale} ${height / scale}`)
    }

    setPath(path) {
        this.ref.path.setAttribute('d', path)
        this.focusPath()
        this.reset()
        this.renderBox()
        this.save()
    }

    reset() {
        const polygonBox = this.ref.path.getBoundingClientRect()
        const previewBox = this.ref.preview.getBoundingClientRect()

        this.box = {
            x: polygonBox.left - previewBox.left,
            y: polygonBox.top - previewBox.top,
            width: polygonBox.width,
            height: polygonBox.height
        }
    }

    save() {
        const pathBox = this.ref.path.getBoundingClientRect()
        const box = this.ref.box.getBoundingClientRect()

        const params = {
            x: box.left - pathBox.left,
            y: box.top - pathBox.top,
            width: box.width,
            height: box.height
        }

        Object.keys(params)
            .filter(key => this.ref[key])
            .forEach(key => this.ref[key].value = Math.round(params[key]/this.scale))
    }

}

const scope = document.getElementById('location-vector')

if (scope) {
    new LocationVector(scope)
}